import { createContext, useContext, useEffect, useId, useRef, useState } from 'react';

import type { ReactNode } from 'react';

/** Saves what an editor holds and has not saved yet; resolves to whether it was saved. */
export type PendingSave = () => Promise<boolean>;

const PendingSavesContext = createContext<Map<string, PendingSave> | null>(null);

const usePendingSavesOf = (hookName: string) => {
    const pendingSaves = useContext(PendingSavesContext);
    if (!pendingSaves) {
        throw new Error(`${hookName} must be used within a <PendingSavesScope>.`);
    }
    return pendingSaves;
};

export const PendingSavesScope = ({ children }: { children: ReactNode }) => {
    const [pendingSaves] = useState(() => new Map<string, PendingSave>());

    return (
        <PendingSavesContext.Provider value={pendingSaves}>{children}</PendingSavesContext.Provider>
    );
};

export const usePendingSave = (save: PendingSave | null) => {
    const pendingSaves = usePendingSavesOf('usePendingSave');
    const id = useId();
    // The latest save is read when it runs, so a new closure each render never re-registers it.
    const latestSave = useRef(save);
    useEffect(() => {
        latestSave.current = save;
    });

    const hasPendingSave = save !== null;
    useEffect(() => {
        if (!hasPendingSave) {
            return;
        }

        pendingSaves.set(id, () => latestSave.current?.() ?? Promise.resolve(true));
        return () => {
            pendingSaves.delete(id);
        };
    }, [pendingSaves, id, hasPendingSave]);
};

/** Resolves `true` once every editor of the scope has saved what it held. */
export const useSavePendingEdits = () => {
    const pendingSaves = usePendingSavesOf('useSavePendingEdits');

    return async () => {
        const results = await Promise.all([...pendingSaves.values()].map((save) => save()));
        return results.every(Boolean);
    };
};
